import { Button, Typography } from '@mui/material'
import { Stack } from '@mui/system'
import React, { ReactNode, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppLocale } from '../../i18n/localeUtils'
import {
    formatBigMoney,
    formatDateYYYYMMDD,
    formatMoney,
    formatNumber,
    formatPercentage,
} from '../../packages/utils/numberFormatters'
import { FactsTable, FactsTableRow, FactsTableRowFlexibleColumns } from '../components/FactsTable'
import { findSubscriptionPeriodCloseToDate, getAmountForPriceAtDate, hasSubPeriodPrelPrice } from '../../utils/warrants'
import { WarrantListItem } from '@finance-lab/utils/types'

const WarrantTerms = ({ warrant }: { warrant: WarrantListItem }) => {
    const { t } = useTranslation()
    const { language: locale } = useAppLocale()

    const tableRows: ReactNode[] = []

    const sharesText = { singular: t('pages.warrants.shareSingular'), plural: t('pages.warrants.sharePlural') }
    const warrantsText = { singular: t('pages.warrants.warrantSingular'), plural: t('pages.warrants.warrantPlural') }
    const quantityLabel = (
        quantity: number | null,
        text: {
            singular: string
            plural: string
        },
    ) => {
        return `${formatNumber(quantity, { locale, decimals: 0 })} ${quantity === 1 ? text.singular : text.plural}`
    }

    const subscriptionPeriods = warrant.subscriptionPeriods
    const [startPeriodIndex, setStartPeriodIndex] = React.useState(0)

    useEffect(() => {
        const currentPeriod = findSubscriptionPeriodCloseToDate({ subscriptionPeriods }, new Date().toISOString())
        setStartPeriodIndex(subscriptionPeriods.findIndex((subPeriod) => subPeriod.id === currentPeriod?.id))
    }, [subscriptionPeriods])

    const subscriptionPeriodsRange = subscriptionPeriods.slice(startPeriodIndex, startPeriodIndex + 1)

    if (subscriptionPeriods && subscriptionPeriods.length > 0) {
        tableRows.push(
            <FactsTableRowFlexibleColumns
                key={tableRows.length}
                firstColumnValue={t('pages.warrants.subscriptionPeriod')}
                columns={subscriptionPeriodsRange}
                getColumnValue={(subscriptionPeriod) => {
                    const startDate = formatDateYYYYMMDD(subscriptionPeriod.subStartDate, { locale })
                    const endDate = formatDateYYYYMMDD(subscriptionPeriod.subEndDate, { locale })
                    return `${startDate} - ${endDate}`
                }}
                lastColumnValue={null}
            />,
        )

        tableRows.push(
            <FactsTableRowFlexibleColumns
                key={tableRows.length}
                firstColumnValue={t('pages.warrants.ratio')}
                columns={subscriptionPeriodsRange}
                getColumnValue={(subscriptionPeriod) =>
                    `${quantityLabel(subscriptionPeriod.warrantsRequired, warrantsText)} => ${quantityLabel(
                        subscriptionPeriod.sharesReceived,
                        sharesText,
                    )}`
                }
                lastColumnValue={null}
            />,
        )

        tableRows.push(
            <FactsTableRowFlexibleColumns
                key={tableRows.length}
                firstColumnValue={t('entities.warrant.fields.warrantSubPrice')}
                columns={subscriptionPeriodsRange}
                getColumnValue={(subscriptionPeriod) => {
                    const { warrantSubPrice, prelSubPrice, sharesReceived } = subscriptionPeriod
                    const showPrelPrice = hasSubPeriodPrelPrice(subscriptionPeriod)
                    let formattedMoney = formatMoney(showPrelPrice ? prelSubPrice : warrantSubPrice, {
                        minDecimals: 2,
                        maxDecimals: 5,
                        locale,
                    })
                    if (formattedMoney && sharesReceived && sharesReceived > 1) {
                        formattedMoney = `${formattedMoney} ${t('site.pricing.perShare')}`
                    }

                    return (
                        <>
                            {formattedMoney || '-'}
                            {showPrelPrice && '*'}
                        </>
                    )
                }}
                lastColumnValue={null}
            />,
        )

        if (warrant.closePrice !== null) {
            tableRows.push(
                <FactsTableRowFlexibleColumns
                    key={tableRows.length}
                    firstColumnValue={t('pages.warrants.currentSharePrice')}
                    columns={subscriptionPeriodsRange}
                    getColumnValue={() => {
                        return (
                            <>
                                {formatMoney(warrant.closePrice, {
                                    minDecimals: 2,
                                    maxDecimals: 5,
                                    locale,
                                })}
                            </>
                        )
                    }}
                    lastColumnValue={null}
                />,
            )
        }

        tableRows.push(
            <FactsTableRowFlexibleColumns
                key={tableRows.length}
                firstColumnValue={t('pages.warrants.amountAtFullUtilization')}
                columns={subscriptionPeriodsRange}
                getColumnValue={(subscriptionPeriod) => {
                    const { subscriptionPeriods, totalNumberOfWarrants } = warrant
                    const { warrantSubPrice, prelSubPrice, subStartDate } = subscriptionPeriod
                    const showPrelPrice = hasSubPeriodPrelPrice(subscriptionPeriod)
                    const price = showPrelPrice ? prelSubPrice : warrantSubPrice
                    const amount =
                        subStartDate === null
                            ? null
                            : getAmountForPriceAtDate(
                                  price,
                                  totalNumberOfWarrants,
                                  subscriptionPeriods,
                                  new Date(subStartDate).toISOString(),
                              )

                    return (
                        <>
                            {formatBigMoney(amount, {
                                currencySymbol: warrant.currency,
                                decimals: 0,
                                locale,
                                tooltip: true,
                            }) || '-'}
                            {showPrelPrice && '*'}
                        </>
                    )
                }}
                lastColumnValue={null}
            />,
        )

        tableRows.push(
            <FactsTableRowFlexibleColumns
                key={tableRows.length}
                firstColumnValue={t('pages.warrants.pricing')}
                columns={subscriptionPeriodsRange}
                getColumnValue={(subscriptionPeriod) => {
                    const vwapDays =
                        subscriptionPeriod.vwapDays !== null && subscriptionPeriod.vwapDays > 0
                            ? subscriptionPeriod.vwapDays
                            : 0
                    return (
                        <>
                            {!!subscriptionPeriod.isVwap && (
                                <div>
                                    <Typography variant='body1'>
                                        {`${t('site.pricing.vwapDiscount')} ${formatPercentage(
                                            subscriptionPeriod?.vwapDiscount,
                                            {
                                                locale,
                                                minDecimals: 0,
                                            },
                                        )}${vwapDays > 0 ? ` (${vwapDays} ${vwapDays > 1 ? t('site.pricing.days') : t('site.pricing.day')})` : null}`}
                                    </Typography>
                                    <Typography variant='body1'>
                                        {t('site.pricing.min')}{' '}
                                        {subscriptionPeriod?.priceMin
                                            ? formatMoney(subscriptionPeriod?.priceMin, {
                                                  minDecimals: 2,
                                                  maxDecimals: 5,
                                                  locale,
                                              })
                                            : t('site.pricing.parValue')}
                                        , {t('site.pricing.max')}{' '}
                                        {subscriptionPeriod?.priceMax
                                            ? formatMoney(subscriptionPeriod?.priceMax, {
                                                  minDecimals: 2,
                                                  maxDecimals: 5,
                                                  locale,
                                              })
                                            : '-'}
                                    </Typography>
                                </div>
                            )}
                            {!subscriptionPeriod.isVwap && <span>{t('site.pricing.fixedPrice')}</span>}
                        </>
                    )
                }}
                lastColumnValue={null}
            />,
        )

        tableRows.push(
            <FactsTableRowFlexibleColumns
                key={tableRows.length}
                firstColumnValue={t('pages.warrants.secured')}
                columns={subscriptionPeriodsRange}
                getColumnValue={(subscriptionPeriod) => {
                    const hasCommitments = subscriptionPeriod.commitments != null
                    const hasGuarantors = subscriptionPeriod.guarantors != null

                    if (hasCommitments || hasGuarantors) {
                        return (
                            <div>
                                {hasCommitments && (
                                    <Typography variant='body1'>
                                        {t('pages.warrants.subscriptionCommitment')}{' '}
                                        {formatPercentage(subscriptionPeriod.commitments, {
                                            locale,
                                            minDecimals: 0,
                                        })}
                                    </Typography>
                                )}
                                {hasGuarantors && (
                                    <Typography variant='body1'>
                                        {t('pages.warrants.guarantorCommitment')}{' '}
                                        {formatPercentage(subscriptionPeriod.guarantors, {
                                            locale,
                                            minDecimals: 0,
                                        })}
                                    </Typography>
                                )}
                            </div>
                        )
                    }
                }}
                lastColumnValue={null}
            />,
        )

        const hasPrelPrice = subscriptionPeriodsRange.find(hasSubPeriodPrelPrice)

        if (hasPrelPrice) {
            const vwapDays =
                subscriptionPeriodsRange.length &&
                subscriptionPeriodsRange[0]?.vwapDays !== null &&
                subscriptionPeriodsRange[0]?.vwapDays > 0
                    ? subscriptionPeriodsRange[0].vwapDays
                    : 0
            if (vwapDays <= 1) {
                tableRows.push(
                    <FactsTableRow key='preliminary-price-info' label={t('pages.warrants.preliminaryOneDay')} />,
                )
            } else {
                tableRows.push(
                    <FactsTableRow
                        key='preliminary-price-info'
                        label={t('pages.warrants.preliminaryDays', { n: vwapDays })}
                    />,
                )
            }
        }
    }

    if (tableRows.length === 0) {
        return (
            <div>
                <Typography variant='body1'>{t('pages.warrants.noSubscriptionPeriods')}</Typography>
            </div>
        )
    }

    return (
        <div>
            <FactsTable rows={tableRows} />
            {subscriptionPeriods.length > 1 && (
                <>
                    <Stack
                        direction={'row'}
                        sx={{
                            justifyContent: 'space-between',
                            my: 6,
                        }}
                    >
                        <Button
                            disabled={startPeriodIndex === 0}
                            onClick={() => {
                                if (startPeriodIndex > 0) setStartPeriodIndex(startPeriodIndex - 1)
                            }}
                        >
                            {t('site.pagination.previous')}
                        </Button>
                        <Button
                            disabled={startPeriodIndex === subscriptionPeriods.length - 1}
                            onClick={() => {
                                if (startPeriodIndex < subscriptionPeriods.length - 1)
                                    setStartPeriodIndex(startPeriodIndex + 1)
                            }}
                        >
                            {t('site.pagination.next')}
                        </Button>
                    </Stack>
                    <Typography variant='body1'>
                        {t('pages.warrants.showingPeriod', {
                            current: startPeriodIndex + 1,
                            total: subscriptionPeriods.length,
                        })}
                    </Typography>
                </>
            )}
        </div>
    )
}

export default WarrantTerms
